import { c as _c } from "react/compiler-runtime";
import React from "react";
import * as prod from "react/jsx-runtime";
import type { Root } from "hast";
import { unified } from "unified";
import rehypeParse from "rehype-parse";
import rehypeReact, { Components } from "rehype-react";
import rehypeSanitize from "rehype-sanitize";
import { cn } from "../../utils/cn";
import { canonicalRichTextSchema } from "../../utils/rich-text/canonical-schema";
import { rehypeCanonicalize } from "../../utils/rich-text/rehype-canonicalize";
import { richTextClasses, richTextRootClass } from "./rich-text-formatting";

// The single rich-text renderer for the product. Every app renders stored HTML
// through this component — there is no per-app fork and no per-call styling
// override. It sanitizes with the canonical pipeline (so display agrees with
// the persist/load seams) and maps the canonical tag set to plain semantic
// elements styled from `rich-text-formatting` (the one source of truth, shared
// with the editor). Anything not mapped falls through to a host element.

const NBSP = " ";

// A paragraph with no meaningful children is an intentional blank line — both
// TipTap and the legacy Redactor editor emit an empty `<p>` to hold vertical
// space. A truly-empty block-level `<p>` collapses to zero height (no line
// box), so we give it a non-breaking space: that reproduces exactly one line of
// the paragraph's own line-height, matching how the editor renders the blank
// line. `<p><br></p>` and `<p>&nbsp;</p>` already have a line box and are left
// untouched.
function isBlankChildren(children: React.ReactNode): boolean {
  if (children == null) return true;
  if (Array.isArray(children)) return children.length === 0;
  if (typeof children === "string") return children.trim() === "";
  return false;
}
const Strong = t0 => {
  const $ = _c(6);
  let children;
  let rest;
  if ($[0] !== t0) {
    ({
      children,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = rest;
  } else {
    children = $[1];
    rest = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== rest) {
    t1 = <strong className={richTextClasses.strong} {...rest}>{children}</strong>;
    $[3] = children;
    $[4] = rest;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};

// Headings share the size-agnostic content-heading style; the per-level size
// comes from HEADING_SCALE on the container (matches the editor). Rendered with
// the real tag for semantics.
const Heading1 = t0 => {
  const $ = _c(6);
  let children;
  let rest;
  if ($[0] !== t0) {
    ({
      children,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = rest;
  } else {
    children = $[1];
    rest = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== rest) {
    t1 = <h1 className={richTextClasses.heading} {...rest}>{children}</h1>;
    $[3] = children;
    $[4] = rest;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
const Heading2 = t0 => {
  const $ = _c(6);
  let children;
  let rest;
  if ($[0] !== t0) {
    ({
      children,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = rest;
  } else {
    children = $[1];
    rest = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== rest) {
    t1 = <h2 className={richTextClasses.heading} {...rest}>{children}</h2>;
    $[3] = children;
    $[4] = rest;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
const Heading3 = t0 => {
  const $ = _c(6);
  let children;
  let rest;
  if ($[0] !== t0) {
    ({
      children,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = rest;
  } else {
    children = $[1];
    rest = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== rest) {
    t1 = <h3 className={richTextClasses.heading} {...rest}>{children}</h3>;
    $[3] = children;
    $[4] = rest;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
const Paragraph = t0 => {
  const $ = _c(6);
  let children;
  let rest;
  if ($[0] !== t0) {
    ({
      children,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = rest;
  } else {
    children = $[1];
    rest = $[2];
  }
  const t1 = isBlankChildren(children) ? NBSP : children;
  let t2;
  if ($[3] !== rest || $[4] !== t1) {
    t2 = <p className={richTextClasses.paragraph} {...rest}>{t1}</p>;
    $[3] = rest;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};

// External web links open in a new tab so a click doesn't navigate away from
// the note, and carry rel="noopener noreferrer" to block reverse-tabnabbing and
// referrer leakage. mailto:/tel: hand off to the OS handler, so they keep the
// same-tab default. An explicit target on legacy content is respected as-is.
const Link = t0 => {
  const $ = _c(17);
  let children;
  let href;
  let rel;
  let rest;
  let target;
  if ($[0] !== t0) {
    ({
      children,
      href,
      target,
      rel,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = href;
    $[3] = rel;
    $[4] = rest;
    $[5] = target;
  } else {
    children = $[1];
    href = $[2];
    rel = $[3];
    rest = $[4];
    target = $[5];
  }
  let t1;
  if ($[6] !== href) {
    t1 = typeof href === "string" && /^https?:\/\//i.test(href);
    $[6] = href;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const isExternalHttp = t1;
  const openInNewTab = target === "_blank" || target == null && isExternalHttp;
  let t2;
  if ($[8] !== openInNewTab || $[9] !== rel || $[10] !== target) {
    t2 = openInNewTab ? {
      target: "_blank",
      rel: rel ?? "noopener noreferrer"
    } : target != null ? {
      target,
      rel
    } : {};
    $[8] = openInNewTab;
    $[9] = rel;
    $[10] = target;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== children || $[13] !== href || $[14] !== rest || $[15] !== t2) {
    t3 = <a href={href} className={richTextClasses.link} {...t2} {...rest}>{children}</a>;
    $[12] = children;
    $[13] = href;
    $[14] = rest;
    $[15] = t2;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return t3;
};
const OrderedList = t0 => {
  const $ = _c(6);
  let children;
  let rest;
  if ($[0] !== t0) {
    ({
      children,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = rest;
  } else {
    children = $[1];
    rest = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== rest) {
    t1 = <ol className={richTextClasses.orderedList} {...rest}>{children}</ol>;
    $[3] = children;
    $[4] = rest;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
const UnorderedList = t0 => {
  const $ = _c(6);
  let children;
  let rest;
  if ($[0] !== t0) {
    ({
      children,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = rest;
  } else {
    children = $[1];
    rest = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== rest) {
    t1 = <ul className={richTextClasses.bulletList} {...rest}>{children}</ul>;
    $[3] = children;
    $[4] = rest;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
const ListItem = t0 => {
  const $ = _c(6);
  let children;
  let rest;
  if ($[0] !== t0) {
    ({
      children,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = rest;
  } else {
    children = $[1];
    rest = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== rest) {
    t1 = <li className={richTextClasses.listItem} {...rest}>{children}</li>;
    $[3] = children;
    $[4] = rest;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};

// Plain semantic table family — legible native elements, decoupled from any
// app's data-grid component.
const Table = t0 => {
  const $ = _c(6);
  let children;
  let rest;
  if ($[0] !== t0) {
    ({
      children,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = rest;
  } else {
    children = $[1];
    rest = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== rest) {
    t1 = <table className={richTextClasses.table} {...rest}>{children}</table>;
    $[3] = children;
    $[4] = rest;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
const TableRow = t0 => {
  const $ = _c(6);
  let children;
  let rest;
  if ($[0] !== t0) {
    ({
      children,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = rest;
  } else {
    children = $[1];
    rest = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== rest) {
    t1 = <tr className={richTextClasses.tableRow} {...rest}>{children}</tr>;
    $[3] = children;
    $[4] = rest;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
const TableHeaderCell = t0 => {
  const $ = _c(6);
  let children;
  let rest;
  if ($[0] !== t0) {
    ({
      children,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = rest;
  } else {
    children = $[1];
    rest = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== rest) {
    t1 = <th className={richTextClasses.tableHead} {...rest}>{children}</th>;
    $[3] = children;
    $[4] = rest;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
const TableCell = t0 => {
  const $ = _c(6);
  let children;
  let rest;
  if ($[0] !== t0) {
    ({
      children,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = rest;
  } else {
    children = $[1];
    rest = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== rest) {
    t1 = <td className={richTextClasses.tableCell} {...rest}>{children}</td>;
    $[3] = children;
    $[4] = rest;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
const components: Partial<Components> = {
  strong: Strong,
  h1: Heading1,
  h2: Heading2,
  h3: Heading3,
  p: Paragraph,
  a: Link,
  ol: OrderedList,
  ul: UnorderedList,
  li: ListItem,
  table: Table,
  tr: TableRow,
  th: TableHeaderCell,
  td: TableCell
};

// rehype plugin: drop a leading top-level `<br>`, which some legacy content
// (e.g. estimate email messages) prefixes and which would otherwise render as
// an unwanted blank first line. Runs on the hast tree (post-sanitize) rather
// than on a serialized string, so display stays a single parse.
function rehypeDropLeadingBreak() {
  return (tree: Root) => {
    const [first] = tree.children;
    if (first?.type === "element" && first.tagName === "br") {
      tree.children.shift();
    }
  };
}

// One frozen pipeline, mirroring `SanitizedHtml`: parse the stored HTML,
// canonicalize + allowlist-sanitize it, then build the React tree directly —
// no intermediate sanitized *string* and no second parse. `rehypeReact` only
// ever receives sanitized hast (and never uses `dangerouslySetInnerHTML`), so
// sanitization is enforced inline rather than left as an invariant a caller
// must uphold. This runs the same canonical rules as the persist seam, so
// display and stored HTML agree.
const processor = unified().use(rehypeParse, {
  fragment: true
}).use(rehypeCanonicalize).use(rehypeSanitize, canonicalRichTextSchema).use(rehypeDropLeadingBreak).use(rehypeReact, {
  Fragment: React.Fragment,
  jsx: prod.jsx,
  jsxs: prod.jsxs,
  createElement: React.createElement,
  components
}).freeze();
export function RichTextRenderer(t0) {
  const $ = _c(11);
  const {
    text,
    className
  } = t0;
  if (!text || !text.trim()) {
    let t1;
    if ($[0] !== className) {
      t1 = cn(richTextRootClass, className);
      $[0] = className;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <div className={t1} />;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] !== text) {
    t1 = processor.processSync(text);
    $[4] = text;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const file = t1;
  let t2;
  if ($[6] !== className) {
    t2 = cn(richTextRootClass, className);
    $[6] = className;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = file.result as React.ReactNode;
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = <div className={t2}>{t3}</div>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}